import { api } from '@mbfab/client-api'
import { isNull, isSome } from '@mbfab/shared'
import { createFileRoute } from '@tanstack/react-router'

import { AppSidebarContainer } from '@/components/app/app-sidebar-container'
import { GraphqlErrorPanel } from '@/components/app/graphql-error-panel'
import { UsersWorkspace } from '@/components/screens/users-workspace'
import { classifyGraphqlError } from '@/lib/apollo/error-classification'
import { userFacingErrorMessage } from '@/lib/user-facing-error-message'
import { useMe } from '@/providers/me-context'

export const Route = createFileRoute('/t/$tenant_id/users')({
  component: UsersPage,
})

function UsersPage() {
  const { tenant_id: tenantId } = Route.useParams()
  return (
    <div className="flex min-h-screen">
      <AppSidebarContainer tenantId={tenantId} />
      <main className="min-w-0 flex-1">
        <UsersContainer />
      </main>
    </div>
  )
}

function UsersContainer() {
  const me = useMe()
  const client = api.useClient()
  const usersQuery = api.useFetchUsersQuery()

  // エラー表示は「まだ一度も取得できていない」ときだけにする。
  // 一度表示できた後の refetch 失敗で画面ごとエラーパネルに差し替わると、
  // 直前の操作結果バナーごと消えて復帰手段がリロードしか無くなる
  if (isNull(usersQuery.data) && isSome(usersQuery.error)) {
    // ユーザー管理は manager 以上でないと使えない（resolver の throwIfNotAuthorizedOrNotManager）。
    // member は親 route（isManagerOnlyPath）で先に止めるが、開いている間にロールを下げられた場合などに
    // API が拒否したときも理由が分かる文言にする
    if (classifyGraphqlError(usersQuery.error) === 'forbidden') {
      return (
        <GraphqlErrorPanel
          layout="section"
          classification="forbidden"
          subject="ユーザー一覧"
          description="ユーザーの管理は管理者のみが行えます。組織の管理者にご依頼ください。"
          onRetry={() => {
            void usersQuery.refetch()
          }}
        />
      )
    }
    return (
      <GraphqlErrorPanel
        layout="section"
        error={usersQuery.error}
        subject="ユーザー一覧"
        onRetry={() => {
          void usersQuery.refetch()
        }}
      />
    )
  }

  // mutation 本体と一覧の再取得は try を分ける。同じ try に入れると
  // 「招待は成功したのに refetch が落ちた」ケースで「招待に失敗しました」と報告してしまい、
  // ユーザーが再送信して「既に登録済みのメールアドレスです」に突き当たる
  const runMutation = async (
    mutate: () => Promise<unknown>,
    failureMessage: string,
  ): Promise<{ ok: true } | { ok: false; message: string }> => {
    try {
      await mutate()
    } catch (error) {
      return { ok: false, message: userFacingErrorMessage(error, failureMessage) }
    }
    try {
      await usersQuery.refetch()
    } catch {
      // 再取得の失敗を「操作の失敗」として報告しない（操作自体は成功しているため。
      // 失敗扱いにするとユーザーが再送信して「既に登録済み」等に突き当たる）。
      // 一覧が古いままになるが、Apollo 側のエラー状態は次のレンダーで拾われる
    }
    return { ok: true }
  }

  return (
    <UsersWorkspace
      // 初回ロード中は data が無いため空配列にフォールバックする。DataTable 側の loading が
      // skeleton を出すため、ページヘッダー・検索欄は常にマウントしたまま中身だけが差し替わる
      // （画面まるごとの CenteredLoading をやめる。2026/08 レビューFB対応）
      users={usersQuery.data?.users ?? []}
      currentUserId={me.id}
      loading={usersQuery.loading}
      onInvite={async (input) =>
        await runMutation(async () => await client.createInvitation({ input }), '招待に失敗しました')
      }
      onUpdate={async (input) =>
        await runMutation(async () => await client.updateUser({ input }), '更新に失敗しました')
      }
      onDelete={async (id) => await runMutation(async () => await client.deleteUser({ id }), '削除に失敗しました')}
    />
  )
}
